import { Controller } from "@hotwired/stimulus";

/*
 * SubmitResultView.swift, as a page: every value is a row that opens a sheet, and the sheet is
 * where the picking happens. The form the server sees is unchanged - hidden inputs carry
 * `playedAt`, `retiredSideId`, `winnerSideId` and the per-set scores, and this is what writes to
 * them.
 *
 * The wheel is a scroller: the options are list items, the browser's scroll snapping lands on one,
 * and the value is `scrollTop / rowHeight`. Nothing here listens for a scroll, because the only
 * moment the value matters is when the sheet is confirmed.
 */
type ScoreMode = "regular" | "tie-break";

/*
 * What a `data-action` here fires on.
 *
 * Both, not just the input: the result type and the score mode are radio inputs in a segmented
 * control, but choosing a side or a set score is a `<button value="...">` row in a sheet. Both
 * carry `value` and `dataset`, and narrowing to one of them would have thrown on the other.
 */
type ValueControl = HTMLButtonElement | HTMLInputElement;

const FALLBACK_ROW = 34;
const HOURS = 23;
const MINUTES = 59;

export default class extends Controller<HTMLElement> {
  static override targets = [
    "action",
    "dateInput",
    "dateSheet",
    "dateValue",
    "hourWheel",
    "minuteWheel",
    "playedAt",
    "resultTypeOption",
    "resultTypeSheet",
    "resultTypeValue",
    "retiredRow",
    "retiredSheet",
    "retiredSideId",
    "retiredValue",
    "scoreMode",
    "scoreSheet",
    "scoreSheetTitle",
    "scoreWheel",
    "setScore",
    "tab",
    "timeSheet",
    "timeValue",
    "winnerRow",
    "winnerSheet",
    "winnerSideId",
    "winnerValue",
  ];

  static override values = {
    maxGames: { type: Number, default: 7 },
    maxTieBreak: { type: Number, default: 20 },
  };

  declare readonly actionTarget: HTMLFormElement;
  declare readonly dateInputTarget: HTMLInputElement;
  declare readonly dateSheetTarget: HTMLDialogElement;
  declare readonly dateValueTarget: HTMLElement;
  declare readonly hourWheelTarget: HTMLElement;
  declare readonly maxGamesValue: number;
  declare readonly maxTieBreakValue: number;
  declare readonly minuteWheelTarget: HTMLElement;
  declare readonly playedAtTarget: HTMLInputElement;
  declare readonly resultTypeOptionTargets: HTMLInputElement[];
  declare readonly resultTypeSheetTarget: HTMLDialogElement;
  declare readonly resultTypeValueTarget: HTMLElement;
  declare readonly retiredRowTarget: HTMLElement;
  declare readonly retiredSheetTarget: HTMLDialogElement;
  declare readonly retiredSideIdTarget: HTMLInputElement;
  declare readonly retiredValueTarget: HTMLElement;
  declare readonly scoreModeTargets: HTMLInputElement[];
  declare readonly scoreSheetTarget: HTMLDialogElement;
  declare readonly scoreSheetTitleTarget: HTMLElement;
  declare readonly scoreWheelTarget: HTMLElement;
  declare readonly setScoreTargets: HTMLElement[];
  declare readonly tabTargets: HTMLElement[];
  declare readonly timeSheetTarget: HTMLDialogElement;
  declare readonly timeValueTarget: HTMLElement;
  declare readonly winnerRowTarget: HTMLElement;
  declare readonly winnerSheetTarget: HTMLDialogElement;
  declare readonly winnerSideIdTarget: HTMLInputElement;
  declare readonly winnerValueTarget: HTMLElement;

  /*
   * Which of a set's two numbers each set's sheet is dialling, kept per set the way the app keeps
   * `scoreModeBySetIndex`: picking the tie-break for set two should not change what set one
   * opens on.
   */
  private readonly scoreModeBySet = new Map<number, ScoreMode>();

  private resultType = "";
  private scoreSet = 0;
  private scoreSide = "";

  override connect(): void {
    this.buildWheel(
      this.hourWheelTarget,
      this.range(0, HOURS).map((it) => this.pad(it)),
    );
    this.buildWheel(
      this.minuteWheelTarget,
      this.range(0, MINUTES).map((it) => this.pad(it)),
    );

    const action = new URL(window.location.href).searchParams.get("action");
    this.changeAction(action === null ? "regular" : action.substring(7));
  }

  /* ----------------------------------------------------------------- result type ---- */

  openResultType(): void {
    this.markSelected(this.resultTypeSheetTarget, this.resultType);
    this.resultTypeSheetTarget.showModal();
  }

  onChangeResultType(event: Event): void {
    this.changeAction(this.eventValue(event).toLowerCase());
  }

  openRetiredSide(): void {
    this.markSelected(this.retiredSheetTarget, this.retiredSideIdTarget.value);
    this.retiredSheetTarget.showModal();
  }

  onChangeRetiredSide(event: Event): void {
    const option = this.eventTarget(event);

    this.retiredSideIdTarget.value = option.value;
    this.retiredValueTarget.textContent = this.optionLabel(option);
    this.markSelected(this.retiredSheetTarget, option.value);
  }

  openWinnerSide(): void {
    this.markSelected(this.winnerSheetTarget, this.winnerSideIdTarget.value);
    this.winnerSheetTarget.showModal();
  }

  onChangeWinnerSide(event: Event): void {
    const option = this.eventTarget(event);

    this.winnerSideIdTarget.value = option.value;
    this.winnerValueTarget.textContent = this.optionLabel(option);
    this.markSelected(this.winnerSheetTarget, option.value);
  }

  /* The route decides which model the server binds, so the result type is a change of `action`
       as well as of what the screen shows. */
  changeAction(selected: string): void {
    this.resultType = selected.toUpperCase();

    const actionUrl = new URL(
      this.actionTarget.getAttribute("action") ?? "",
      window.location.href,
    );
    actionUrl.searchParams.set("action", `submit-${selected}`);
    this.actionTarget.setAttribute("action", actionUrl.toString());

    const option = this.resultTypeOptionTargets.find(
      (it) => it.value === this.resultType,
    );
    if (option !== undefined) {
      this.resultTypeValueTarget.textContent = this.optionLabel(option);
    }

    this.markSelected(this.resultTypeSheetTarget, this.resultType);

    this.tabTargets.forEach((it) => {
      it.hidden = it.id !== `submit-score-tab-${selected}`;
    });

    /* A row only exists for the result type that needs it, and the input behind it is disabled
           while the row is away: a disabled control posts nothing, so a walkover cannot carry a
           retired side. */
    this.toggleSide(
      this.retiredRowTarget,
      this.retiredSideIdTarget,
      selected === "retirement",
    );
    this.toggleSide(
      this.winnerRowTarget,
      this.winnerSideIdTarget,
      selected === "walkover",
    );

    /* A walkover has no games at all. */
    this.setScoreTargets.forEach((section) => {
      section.hidden = selected === "walkover";
      section.querySelectorAll("input").forEach((input) => {
        input.disabled = selected === "walkover";
      });
    });
  }

  toggleSide(
    row: HTMLElement,
    input: HTMLInputElement,
    isShown: boolean,
  ): void {
    row.hidden = !isShown;
    input.disabled = !isShown;
    input.required = isShown;
  }

  /* ----------------------------------------------------------------- set score ------ */

  openScore(event: Event): void {
    const row = this.eventTarget(event);
    const section = row.closest<HTMLElement>("[data-set-index]");
    if (section === null) {
      return;
    }

    this.scoreSet = Number(section.dataset.setIndex);
    this.scoreSide = row.dataset.side ?? "";
    this.scoreSheetTitleTarget.textContent = (
      section.querySelector(".wp-list-title")?.textContent ?? ""
    ).trim();

    const mode = this.scoreModeBySet.get(this.scoreSet) ?? "regular";
    this.scoreModeTargets.forEach((it) => {
      it.checked = it.value === mode;
    });

    this.scoreSheetTarget.showModal();
    this.fillScoreWheel(mode);
  }

  onChangeScoreMode(event: Event): void {
    const mode = this.asScoreMode(this.eventValue(event));

    this.scoreModeBySet.set(this.scoreSet, mode);
    this.fillScoreWheel(mode);
  }

  confirmScore(): void {
    const input = this.scoreInput(
      this.scoreSet,
      this.scoreSide,
      this.scoreMode(),
    );
    if (input !== null) {
      input.value = String(this.wheelValue(this.scoreWheelTarget));
    }

    this.refreshScores(this.scoreSet);
    this.scoreSheetTarget.close();
  }

  fillScoreWheel(mode: ScoreMode): void {
    const limit =
      mode === "tie-break" ? this.maxTieBreakValue : this.maxGamesValue;
    const current = Number(
      this.scoreInput(this.scoreSet, this.scoreSide, mode)?.value ?? 0,
    );

    this.buildWheel(this.scoreWheelTarget, this.range(0, limit));
    this.scrollWheel(this.scoreWheelTarget, Math.min(current, limit));
  }

  scoreMode(): ScoreMode {
    const checked = this.scoreModeTargets.find((it) => it.checked);

    return checked === undefined ? "regular" : this.asScoreMode(checked.value);
  }

  scoreInput(
    set: number,
    side: string,
    mode: ScoreMode,
  ): HTMLInputElement | null {
    const section = this.setScoreTargets.find(
      (it) => Number(it.dataset.setIndex) === set,
    );

    return (
      section?.querySelector<HTMLInputElement>(
        `[data-score="${side}-${mode}"]`,
      ) ?? null
    );
  }

  /* Both rows of the set are redrawn, not just the one that was edited: the tie-break shows on a
       row as soon as EITHER side has points on it, the same rule the app's `hasTieBreak` uses and
       the same one a finished match's scoreline follows. */
  refreshScores(set: number): void {
    const section = this.setScoreTargets.find(
      (it) => Number(it.dataset.setIndex) === set,
    );
    if (section === undefined) {
      return;
    }

    const value = (side: string, mode: ScoreMode): number =>
      Number(
        section.querySelector<HTMLInputElement>(
          `[data-score="${side}-${mode}"]`,
        )?.value ?? 0,
      );
    const hasTieBreak =
      value("a", "tie-break") > 0 || value("b", "tie-break") > 0;

    section.querySelectorAll<HTMLElement>("[data-side]").forEach((row) => {
      const side = row.dataset.side ?? "";
      const games = row.querySelector("[data-score-games]");
      if (games !== null) {
        games.textContent = String(value(side, "regular"));
      }

      const tieBreak = row.querySelector<HTMLElement>("[data-score-tie-break]");
      if (tieBreak !== null) {
        tieBreak.textContent = String(value(side, "tie-break"));
        tieBreak.hidden = !hasTieBreak;
      }
    });
  }

  /* ----------------------------------------------------------------- played at ------ */

  openDate(): void {
    this.dateInputTarget.value = this.playedAtTarget.value.substring(0, 10);
    this.dateInputTarget.min = (this.playedAtTarget.min || "").substring(0, 10);
    this.dateInputTarget.max = (this.playedAtTarget.max || "").substring(0, 10);
    this.dateSheetTarget.showModal();

    /* The month grid is drawn from the field, and the field was just given a different value
           and a different window. The sheet is opened here rather than through the library's own
           opener, which is what would otherwise have asked for this. */
    document.dispatchEvent(
      new CustomEvent("wp:paint", { detail: { root: this.dateSheetTarget } }),
    );
  }

  confirmDate(): void {
    if (this.dateInputTarget.value !== "") {
      this.setPlayedAt(
        this.dateInputTarget.value,
        this.playedAtTarget.value.substring(11, 16),
      );
    }

    this.dateSheetTarget.close();
  }

  openTime(): void {
    const [hour, minute] = this.playedAtTarget.value
      .substring(11, 16)
      .split(":");

    this.timeSheetTarget.showModal();
    this.scrollWheel(this.hourWheelTarget, Number(hour ?? 0));
    this.scrollWheel(this.minuteWheelTarget, Number(minute ?? 0));
  }

  confirmTime(): void {
    const hour = this.pad(this.wheelValue(this.hourWheelTarget));
    const minute = this.pad(this.wheelValue(this.minuteWheelTarget));

    this.setPlayedAt(
      this.playedAtTarget.value.substring(0, 10),
      `${hour}:${minute}`,
    );
    this.timeSheetTarget.close();
  }

  /* `playedAt` is one value and the screen shows it as two, so both rows are redrawn from it
       rather than from whichever sheet was just confirmed. */
  setPlayedAt(date: string, time: string): void {
    this.playedAtTarget.value = `${date}T${time}`;

    /* Day, short month, year - the order the server renders it in with `d MMM yyyy`, rather
           than whatever order the locale would put those three in on its own. The month name is
           still the locale's. */
    const parsed = new Date(`${date}T${time}`);
    const locale = document.documentElement.lang || undefined;
    const month = parsed.toLocaleDateString(locale, { month: "short" });

    this.dateValueTarget.textContent = `${parsed.getDate()} ${month} ${parsed.getFullYear()}`;
    this.timeValueTarget.textContent = time;
  }

  /* ----------------------------------------------------------------- wheel ---------- */

  buildWheel(wheel: HTMLElement, values: readonly (number | string)[]): void {
    wheel.replaceChildren(
      ...values.map((value) => {
        const option = document.createElement("li");
        option.className = "wp-wheel-option";
        option.setAttribute("role", "option");
        option.textContent = String(value);

        return option;
      }),
    );
  }

  scrollWheel(wheel: HTMLElement, index: number): void {
    wheel.scrollTo({ behavior: "instant", top: index * this.rowHeight(wheel) });
    this.markWheelValue(wheel, index);
  }

  wheelValue(wheel: HTMLElement): number {
    const index = Math.round(wheel.scrollTop / this.rowHeight(wheel));
    this.markWheelValue(wheel, index);

    return index;
  }

  markWheelValue(wheel: HTMLElement, index: number): void {
    Array.from(wheel.children).forEach((option, position) => {
      option.setAttribute("aria-selected", String(position === index));
    });
  }

  rowHeight(wheel: HTMLElement): number {
    return (
      parseFloat(getComputedStyle(wheel).getPropertyValue("--wp-wheel-row")) ||
      FALLBACK_ROW
    );
  }

  /* ----------------------------------------------------------------- helpers -------- */

  markSelected(sheet: HTMLDialogElement, value: string): void {
    sheet.querySelectorAll<ValueControl>("[value]").forEach((option) => {
      const mark = option.querySelector<HTMLElement>("[data-selected-mark]");
      if (mark !== null) {
        mark.hidden = option.value !== value;
      }
    });
  }

  optionLabel(option: Element): string {
    return (option.querySelector(".wp-list-label")?.textContent ?? "").trim();
  }

  range(from: number, to: number): number[] {
    return Array.from({ length: to - from + 1 }, (_, index) => from + index);
  }

  pad(value: number | string): string {
    return String(value).padStart(2, "0");
  }

  /** The control a `data-action` fired on. */
  private eventTarget(event: Event): ValueControl {
    const target = event.currentTarget;
    if (
      !(target instanceof HTMLButtonElement) &&
      !(target instanceof HTMLInputElement)
    ) {
      throw new TypeError(
        "A submit-score action fired on something with no value to read",
      );
    }

    return target;
  }

  private eventValue(event: Event): string {
    return this.eventTarget(event).value;
  }

  private asScoreMode(value: string): ScoreMode {
    return value === "tie-break" ? "tie-break" : "regular";
  }
}
